import { useEffect, useState, type ReactNode } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root.js';
import { Button } from '../components/ui/Button.js';
import { verifyAgencySignup } from '../lib/agency-signup.js';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email/$token',
  component: VerifyEmailPage,
});

/** Brand chrome shared with the login / signup pages. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3 justify-center">
          <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
            <span className="font-serif text-cream text-lg leading-none">A</span>
          </div>
          <div className="font-serif text-2xl tracking-tighter2 text-ink">Admitly</div>
        </div>
        <div className="bg-white border border-line rounded-2xl shadow-card p-8">{children}</div>
      </div>
    </div>
  );
}

function StateMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="text-center">
      <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">{title}</h1>
      <p className="text-sm text-muted mb-6">{body}</p>
      <Link to="/login">
        <Button variant="secondary" className="w-full">
          Go to login
        </Button>
      </Link>
    </div>
  );
}

function VerifyEmailPage() {
  const { token } = Route.useParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    verifyAgencySignup(token)
      .then(() => {
        if (active) setStatus('success');
      })
      .catch(() => {
        if (active) setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (status === 'loading') {
    return (
      <Shell>
        <div className="flex flex-col items-center py-6">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-navy border-r-transparent" />
          <p className="text-sm text-muted mt-4">Verifying your email…</p>
        </div>
      </Shell>
    );
  }

  if (status === 'success') {
    return (
      <Shell>
        <StateMessage
          title="Your email is verified"
          body="We'll review your application and email you once it's approved."
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <StateMessage
        title="This link is invalid or has expired"
        body="If you started a signup, try the link again from your most recent email."
      />
    </Shell>
  );
}
